import React from "react";

import { AppState } from "store";
import { useSelector } from "react-redux";
import { Map } from "immutable";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TodoExtended } from "models/propTypes/todoPropTypes";

/**
 * Revenue-report todos arrive grouped per template, so periods interleave. Ordering the
 * whole group by period start puts the oldest unapproved period first. Placeholders for
 * not-yet-loaded items keep their trailing position, and the order is shared with the
 * drawer navigation, which addresses todos by their index in this same array.
 */
function sortByPeriodAscending(items: (TodoExtended | undefined)[]) {
  const loaded = items.filter((i): i is TodoExtended => i !== undefined);
  const placeholders = items.length - loaded.length;
  loaded.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return [...loaded, ...Array<TodoExtended | undefined>(placeholders).fill(undefined)];
}

function useTodosByGroup() {
  const { groupMap, itemMap } = useSelector((state: AppState) => state.todos);

  const todosByGroup = React.useMemo(() => {
    const todos = groupMap?.map((t, group) => {
      const total = t.total;
      let items = t.items.map((k) => itemMap?.get(k));
      const fullyLoaded = total === items.count();
      if (!fullyLoaded) {
        items = items.setSize(total);
      }
      const ordered =
        group === WintSuperkollAPIModel.TodoGroup.RevenueReporting
          ? sortByPeriodAscending(items.toArray())
          : items.toArray();
      return { total, items: ordered, fullyLoaded };
    });
    return (
      todos ?? Map<WintSuperkollAPIModel.TodoGroup, { total: number; items: TodoExtended[]; fullyLoaded: boolean }>()
    );
  }, [groupMap, itemMap]);

  return { todosByGroup };
}

export default useTodosByGroup;
